import { ADMIN_LOGIN, AuthUserResponse, ErrorToast } from '@vgrubs/components';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  clearDeviceFcmToken,
  getAccessToken,
  getRefreshToken,
  setNewAccessToken,
} from '../helpers/localstorage-helper';
// A leaf module (see its header) - safe here even though api.ts is reachable
// from the barrel in every app: in builds without the PWA plugin it just
// holds nulls.
import {
  getApplyPendingUpdate,
  getSwRegistration,
} from '../pwa/swRegistration';
import { isDeliveries } from '../helpers/appType';
import { useAuth } from '../store/auth';
import { useDeliveriesOrg } from '../store/deliveriesOrg';

const BASE_URL = process.env.NX_API_URL;
const APP_TYPE = process.env.NX_APP_TYPE || '';

export const headerVersion = () => {
  switch (APP_TYPE) {
    case 'admin':
      return 'webAdmin-version-1.0.0';
    case 'sales':
      return 'webSales-version-1.0.0';
    case 'partner':
      return 'webPartner-version-1.0.0';
    case 'brandOwner':
      return 'web-version-1.0.0';
    case 'deliveries':
      // The vDrive console is served by the vDrive PARTNER API, which knows
      // its clients as webPartner / driverIos / driverAndroid. Nothing to do
      // with the vGrubs partner app: different host, same client name.
      return 'webPartner-version-1.0.0';
    default:
      return 'web-version-1.0.0';
  }
};

let accessToken: string | null = getAccessToken();
let refreshTokenPromise: Promise<string | undefined> | null = null;

interface TokenResponse {
  data: {
    accessToken: string;
    refreshToken?: string;
  };
}

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'app-version': headerVersion(),
    'content-type': 'application/json',
  },
});

/**
 * Forget the token this module caches. A deliberate sign out (`signOut`)
 * must call it: the cache is only refreshed by verify-sms and refresh
 * responses, so without this the revoked token would ride along on the next
 * request, 401, and drive the interceptor into a spurious forced logout.
 */
export const resetAccessTokenCache = () => {
  accessToken = null;
};

/** What every kind of logout drops besides the session itself. */
export const clearSessionScopedState = () => {
  clearDeviceFcmToken();
  accessToken = null;
  // The vDrive console scopes everything to a selected organization; the next
  // account must not inherit it.
  if (isDeliveries) useDeliveriesOrg.getState().setSelectedOrgId(null);
};

const handleLogout = (status: number, error: string) => {
  useAuth.setState({
    user: null,
    tokens: null,
    lastLogout: {
      date: new Date().toISOString(),
      status,
      error,
      place: 'api',
    },
  });
  /*
   * Deliberately no `POST /v1/users/logout` here, unlike the deliberate sign
   * out in `signOut`: this path is reached because the session is already
   * invalid, so the call would 401 and re-enter this same interceptor.
   *
   * Dropping the device token locally is enough. The row on the backend is
   * either pruned when Firebase rejects a push to it, or moved to the next
   * account that signs in on this browser - registering the same token always
   * deletes the older row holding it.
   */
  clearSessionScopedState();
  window.location.href = ADMIN_LOGIN;
};

/*
 * 426 Upgrade Required: the backend's AppVersionMiddleware wants a newer
 * client. Dormant for the web apps today (`headerVersion` is a fixed string),
 * but with a service worker precaching the app shell a stale client could
 * otherwise keep serving its old build forever - so ask the worker for the
 * new version and reload, at most once a minute.
 */
const UPGRADE_RELOAD_AT = 'vg-upgrade-reload-at';
let upgradeInFlight = false;

const waitForWaitingWorker = (
  registration: ServiceWorkerRegistration,
  timeoutMs: number
) =>
  new Promise<ServiceWorker | null>((resolvePromise) => {
    if (registration.waiting) return resolvePromise(registration.waiting);
    const installing = registration.installing;
    if (!installing) return resolvePromise(null);
    const timer = setTimeout(
      () => resolvePromise(registration.waiting),
      timeoutMs
    );
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed') {
        clearTimeout(timer);
        resolvePromise(registration.waiting);
      }
    });
  });

const handleUpgradeRequired = async () => {
  // The tracker app reaches this module during SSR, where there is nothing
  // to reload.
  if (typeof window === 'undefined') return;
  if (upgradeInFlight) return;
  upgradeInFlight = true;

  // One reload per minute per tab, however often the backend keeps answering
  // 426 - the in-memory flag dies with the reload, this survives it.
  const last = Number(sessionStorage.getItem(UPGRADE_RELOAD_AT) || 0);
  if (Date.now() - last < 60 * 1000) return;
  sessionStorage.setItem(UPGRADE_RELOAD_AT, String(Date.now()));

  try {
    const registration = getSwRegistration();
    if (registration) {
      await registration.update();
      const waiting = await waitForWaitingWorker(registration, 10 * 1000);
      const apply = getApplyPendingUpdate();
      if (waiting && apply) {
        // SKIP_WAITING, then reload once the new worker takes control.
        await apply();
        return;
      }
    }
  } catch {
    // Fall through to the plain reload.
  }
  window.location.reload();
};

api.interceptors.request.use(
  (config) => {
    const refreshToken = getRefreshToken();
    if (config.url === '/v1/auth/refresh') {
      config.headers.set('Authorization', `Bearer ${refreshToken}`);
    } else {
      const currentAccessToken = getAccessToken();

      // Always get fresh token from localStorage instead of using stale module variable
      if (accessToken) {
        config.headers.set('Authorization', `Bearer ${accessToken}`);
      } else {
        config.headers.set('Authorization', `Bearer ${currentAccessToken}`);
      }
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.config.url === '/v1/auth/verify-sms') {
      const newToken = (response.data.data as AuthUserResponse).tokens
        .accessToken;
      accessToken = newToken;
      setNewAccessToken(newToken);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    const status = error.response?.status || 0;
    const isRefreshRequest = originalRequest.url === '/v1/auth/refresh';

    if (status === 426) {
      void handleUpgradeRequired();
      return Promise.reject(error);
    }

    if ((status === 401 || status === 417) && isRefreshRequest) {
      handleLogout(status, JSON.stringify(error));
      if (status === 401) {
        ErrorToast('Invalid refresh token. Please log in again.');
      }
      if (status === 417) {
        ErrorToast('Session expired. Please log in again.');
      }
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!refreshTokenPromise) {
        refreshTokenPromise = api
          .post<TokenResponse>('/v1/auth/refresh')
          .then((res) => {
            const newAccessToken = res.data.data.accessToken;
            setNewAccessToken(newAccessToken);
            accessToken = newAccessToken;
            refreshTokenPromise = null;
            return newAccessToken;
          })
          .catch((refreshError) => {
            refreshTokenPromise = null;
            handleLogout(status, JSON.stringify(error));
            return Promise.reject(refreshError);
          });
      }

      try {
        const newToken = await refreshTokenPromise;
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }
        return axios(originalRequest);
      } catch (e) {
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  }
);

export { api };
